/**
 * Study-scoped health metrics strip, rendered at the top of the study Overview tab.
 *
 * Mirrors the portfolio DaeDashboard HealthPanel (donuts + stat tiles) but scoped to a single
 * study. Reuses the shared StatTile and StatusDonut presentational components so both panels render
 * identically.
 */

import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import { type PieChartProps } from "@cloudscape-design/components/pie-chart"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { WorkflowStage } from "@/generated/sdk"
import { useRunMetrics } from "@/hooks/queries/useIngestion"
import StatTile from "../../components/shared/StatTile"
import StatusDonut from "../../components/shared/StatusDonut"

interface StudyMetrics {
  totalDTAs: number
  approvedDTAs: number
  vendorCount: number
  totalRuns: number
  passedRuns: number
  failedRuns: number
  avgLatencyMs: number | null
  stageCounts: Record<WorkflowStage, number>
}

interface StudyHealthPanelProps {
  studyId: string
  metrics: StudyMetrics
  loading: boolean
}

/**
 * Human-friendly labels for the data-stream status donut, in display order.
 */
const STAGE_LABELS: { stage: WorkflowStage; label: string }[] = [
  { stage: WorkflowStage.Active, label: "Active" },
  { stage: WorkflowStage.Approved, label: "Approved" },
  { stage: WorkflowStage.Review, label: "In Review" },
  { stage: WorkflowStage.Draft, label: "Draft" },
  { stage: WorkflowStage.Inactive, label: "Inactive" },
]

const StudyHealthPanel = ({ studyId, metrics, loading }: StudyHealthPanelProps) => {
  // Run metrics come from the ingestion service (not derivable from the DTA list). They add
  // quarantine (totalFailedRecords) and pending-review counts that the derived study metrics lack.
  const { data: runMetrics, isPending: runMetricsLoading } = useRunMetrics(studyId)

  const streamStatusData: PieChartProps.Datum[] = STAGE_LABELS.map(({ stage, label }) => ({
    title: label,
    value: metrics.stageCounts[stage],
  })).filter((d) => d.value > 0)

  const totalRuns = runMetrics?.totalRuns ?? metrics.totalRuns
  const passedRuns = runMetrics?.passedRuns ?? metrics.passedRuns
  const failedRuns = runMetrics?.failedRuns ?? metrics.failedRuns
  const pendingReview = runMetrics?.pendingReviewCount ?? 0
  const quarantinedRecords = runMetrics?.totalFailedRecords ?? 0
  const inProgressRuns = Math.max(totalRuns - passedRuns - failedRuns, 0)

  const runHealthData: PieChartProps.Datum[] = [
    { title: "Passed", value: passedRuns },
    { title: "Failed", value: failedRuns },
    { title: "In Progress", value: inProgressRuns },
  ].filter((d) => d.value > 0)

  const dtasTab = `/studies/${studyId}?tab=dtas`
  const ingestionTab = `/studies/${studyId}?tab=ingestion`
  const avgLatency = metrics.avgLatencyMs ? `${(metrics.avgLatencyMs / 1000).toFixed(1)}s` : "N/A"

  return (
    <Container
      header={
        <Header variant="h2" description="Health metrics for this study">
          Health
        </Header>
      }
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
          <Container header={<Header variant="h3">DTAs by Status</Header>}>
            <StatusDonut
              data={streamStatusData}
              innerMetricValue={String(metrics.totalDTAs)}
              innerMetricDescription="DTAs"
              loading={loading}
              ariaLabel="DTAs by status"
            />
          </Container>
          <Container header={<Header variant="h3">Ingestion Run Health</Header>}>
            <StatusDonut
              data={runHealthData}
              innerMetricValue={String(totalRuns)}
              innerMetricDescription="Runs"
              loading={runMetricsLoading}
              ariaLabel="Ingestion run health"
            />
          </Container>
        </ColumnLayout>

        <ColumnLayout columns={3} minColumnWidth={3}>
          <StatTile
            to={dtasTab}
            iconName="file"
            iconVariant="link"
            value={metrics.totalDTAs}
            label="DTAs"
          />
          <StatTile
            to={dtasTab}
            iconName="status-positive"
            iconVariant="success"
            value={metrics.approvedDTAs}
            label="Active DTAs"
          />
          <StatTile
            to={dtasTab}
            iconName="group"
            iconVariant="link"
            value={metrics.vendorCount}
            label="Vendors"
          />
          <StatTile
            to={ingestionTab}
            iconName="status-warning"
            iconVariant={pendingReview > 0 ? "warning" : "subtle"}
            value={pendingReview}
            label="Pending Review"
          />
          <StatTile
            to={ingestionTab}
            iconName="status-negative"
            iconVariant={quarantinedRecords > 0 ? "error" : "subtle"}
            value={quarantinedRecords}
            label="Quarantined Records"
          />
          <StatTile
            to={ingestionTab}
            iconName="status-pending"
            iconVariant="link"
            value={avgLatency}
            label="Avg Latency"
          />
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  )
}

export default StudyHealthPanel
